import React, {
  useRef,
  useCallback,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  type OpportunitiesWidgetProps,
  type OpportunitiesThemingProps,
  THEMING_PROP_TO_PARAM,
} from "./types";
import { register as registerOverlayHost } from "./shared/campaign-detail-overlay";

/**
 * Ref handle exposed by LegionOpportunities for imperative control.
 */
export interface LegionOpportunitiesHandle {
  /** Reload the widget iframe */
  refresh: () => void;
}

/**
 * Build the iframe src URL from widget props.
 */
function buildWidgetUrl(props: OpportunitiesWidgetProps): string {
  const {
    baseUrl,
    count = 5,
    layout = "horizontal",
    tags,
    theme = "light",
  } = props;

  const url = new URL("/widget/opportunities", baseUrl);
  url.searchParams.set("count", String(Math.min(Math.max(count, 1), 20)));
  url.searchParams.set(
    "layout",
    layout === "vertical" ? "vertical" : "horizontal"
  );

  if (tags) {
    url.searchParams.set("tags", tags);
  }

  if (theme) {
    url.searchParams.set("theme", theme);
  }

  // Append theming params
  const themingKeys = Object.keys(THEMING_PROP_TO_PARAM) as Array<
    keyof OpportunitiesThemingProps
  >;
  for (const key of themingKeys) {
    const value = props[key];
    if (value !== undefined && value !== null && String(value).length > 0) {
      url.searchParams.set(THEMING_PROP_TO_PARAM[key], String(value));
    }
  }

  return url.toString();
}

/**
 * LegionOpportunities – a React component that embeds the Legion
 * Opportunities widget via an iframe.
 *
 * @example
 * ```tsx
 * import { LegionOpportunities } from '@legionhandtech/lht-react-widgets';
 *
 * function App() {
 *   return (
 *     <LegionOpportunities
 *       baseUrl="https://your-legion-domain.com"
 *       count={5}
 *       layout="horizontal"
 *       theme="light"
 *     />
 *   );
 * }
 * ```
 */
export const LegionOpportunities = forwardRef<
  LegionOpportunitiesHandle,
  OpportunitiesWidgetProps
>(function LegionOpportunities(props, ref) {
  const {
    baseUrl,
    count = 5,
    layout = "horizontal",
    tags = "",
    theme,
    height = "auto",
    apiKey,
    className,
    style,
    onLoad,
    onError,
    ...rest
  } = props;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Register this instance with the host-level campaign detail overlay so
  // clicks inside the iframe open a viewport-filling overlay on the host
  // page instead of a modal clipped to the iframe rect.
  useEffect(() => {
    if (!containerRef.current) return;
    const detach = registerOverlayHost({
      element: containerRef.current,
      iframeAccessor: () => iframeRef.current,
      baseUrl: () => baseUrl,
      tokenAccessor: () => apiKey || null,
      themeAccessor: () => theme || "light",
    });
    return detach;
  }, [apiKey, baseUrl, theme]);

  // Expose imperative refresh method
  useImperativeHandle(
    ref,
    () => ({
      refresh: () => {
        const iframe = iframeRef.current;
        if (iframe?.contentWindow) {
          iframe.contentWindow.location.reload();
        }
      },
    }),
    []
  );

  const src = useMemo(
    () => buildWidgetUrl(props),
    [
      baseUrl,
      count,
      layout,
      tags,
      theme,
      // Include all theming props in the dep array via a stable key
      ...Object.keys(THEMING_PROP_TO_PARAM).map(
        (k) => rest[k as keyof typeof rest]
      ),
    ]
  );

  const handleLoad = useCallback(() => {
    const iframe = iframeRef.current;

    // Send API key to iframe if provided
    if (iframe?.contentWindow && apiKey) {
      iframe.contentWindow.postMessage(
        { type: "CONFIG", apiKey, theme },
        baseUrl
      );
    }

    onLoad?.({
      widget: "opportunities",
      count: Math.min(Math.max(count, 1), 20),
      layout: layout === "vertical" ? "vertical" : "horizontal",
      tags: tags || "",
    });
  }, [apiKey, baseUrl, count, layout, tags, theme, onLoad]);

  const handleError = useCallback(() => {
    onError?.("Failed to load opportunities widget");
  }, [onError]);

  const isFullHeight = height === "auto" || height === "100%";
  const minHeight = layout === "vertical" ? "600px" : "320px";

  const containerStyle: React.CSSProperties = {
    display: "block",
    width: "100%",
    backgroundColor: "transparent",
    ...(isFullHeight ? { height: "100%", minHeight } : { height }),
    ...style,
  };

  const iframeStyle: React.CSSProperties = {
    border: "none",
    borderRadius: "8px",
    overflow: "hidden",
    width: "100%",
    ...(isFullHeight ? { height: "100%", minHeight } : { height }),
  };

  return (
    <div ref={containerRef} className={className} style={containerStyle}>
      <iframe
        ref={iframeRef}
        src={src}
        title="Opportunities Widget"
        width="100%"
        scrolling="no"
        style={iframeStyle}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
});
